/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
function isBulletLine(line) {
  return /^(?:\u2022|-)\s*/.test(line);
}

function isNumberedHeading(line) {
  return /^\d+([.]\d+)*[.)]?\s+/.test(line) && line.length <= 120;
}

function normalizeLegalText(text) {
  return (text || "")
    .replace(/\r/g, "")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function renderLegalText(text, palette) {
  const lines = normalizeLegalText(text).split("\n");
  const elements = [];
  let bulletBuffer = [];

  const flushBullets = () => {
    if (!bulletBuffer.length) return;
    elements.push(
      <ul key={`ul-${elements.length}`} style={{ margin: "8px 0 14px", paddingInlineStart: "22px" }}>
        {bulletBuffer.map((item, idx) => (
          <li key={idx} style={{ marginBottom: "6px" }}>
            {item}
          </li>
        ))}
      </ul>
    );
    bulletBuffer = [];
  };

  lines.forEach((raw, idx) => {
    const line = raw.trim();

    if (!line) {
      flushBullets();
      elements.push(<div key={`sp-${idx}`} style={{ height: "10px" }} />);
      return;
    }

    if (isBulletLine(line)) {
      bulletBuffer.push(line.replace(/^(?:\u2022|-)\s*/, ""));
      return;
    }

    flushBullets();

    if (isNumberedHeading(line)) {
      elements.push(
        <h5
          key={`h-${idx}`}
          style={{
            margin: "12px 0 8px",
            fontWeight: 800,
            fontSize: "1.06rem",
            color: palette.accent,
          }}
        >
          {line}
        </h5>
      );
      return;
    }

    elements.push(
      <p key={`p-${idx}`} style={{ margin: "0 0 10px", lineHeight: 2 }}>
        {line}
      </p>
    );
  });

  flushBullets();
  return elements;
}

export default function LegalDocumentModal({
  isOpen,
  onClose,
  title,
  content,
  isArabic,
  themeColors,
  loading = false,
}) {
  if (!isOpen) return null;

  const palette = {
    dark: themeColors?.darkPurple || "#21003D",
    primary: themeColors?.purple || "#7400BF",
    accent: themeColors?.gold || "#F2B441",
    textLight: themeColors?.textLight || "#f3e9ff",
    gradient: themeColors?.gradient || "linear-gradient(135deg, #21003D, #7400BF)",
    cardBackground: "linear-gradient(160deg, rgba(33,0,61,0.96), rgba(116,0,191,0.92))",
    bodyText: "#f8f4ff",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(19, 6, 38, 0.75)",
        zIndex: 10000,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "18px",
        backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "980px",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
          background: palette.cardBackground,
          borderRadius: "18px",
          border: `2px solid ${palette.accent}`,
          boxShadow: "0 24px 60px rgba(0,0,0,0.38)",
          direction: isArabic ? "rtl" : "ltr",
          fontFamily: "'Tajawal', 'Cairo', sans-serif",
          color: palette.bodyText,
          overflow: "hidden",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: `2px solid ${palette.accent}`,
            background: palette.gradient,
          }}
        >
          <h4
            style={{
              margin: 0,
              fontWeight: 800,
              fontSize: "1.55rem",
              letterSpacing: isArabic ? "0" : "0.2px",
              color: palette.textLight,
            }}
          >
            {title}
          </h4>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: palette.accent,
              color: palette.dark,
              borderRadius: "10px",
              padding: "8px 16px",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            {isArabic ? "إغلاق" : "Close"}
          </button>
        </div>

        <div
          style={{
            overflowY: "auto",
            padding: "20px 24px 24px",
            background: "transparent",
            textAlign: "start",
            fontSize: "1.08rem",
          }}
        >
          {loading ? (
            <p style={{ margin: 0 }}>{isArabic ? "جاري تحميل المستند..." : "Loading document..."}</p>
          ) : (
            renderLegalText(content, palette)
          )}
        </div>
      </div>
    </div>
  );
}
